import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  Paper,
  Stack,
  ToggleButton,
  Icon,
  InputAdornment,
  Chip,
  Fade,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Cart, PaymentData } from "../../types/pos.types";

type PaymentMethod = "cash" | "card" | "mobile";

interface PaymentFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payment: PaymentData) => void;
  cart: Cart;
}

const validationSchema = Yup.object({
  method: Yup.string()
    .oneOf(["cash", "card", "mobile"] as const)
    .required("Payment method is required"),
  amount: Yup.number()
    .required("Amount is required")
    .min(0, "Amount must be greater than or equal to 0"),
  reference: Yup.string().when("method", {
    is: "cash",
    then: (schema) => schema,
    otherwise: (schema) =>
      schema.required("Reference number is required for non-cash payments"),
  }),
});

const formatAmount = (amount: number) => Number(amount.toFixed(2));

const QuickAmountButton: React.FC<{
  amount: number;
  onClick: () => void;
  selected?: boolean;
  isIncrement?: boolean;
  variant?: "add" | "subtract";
  size?: "small" | "large";
}> = ({ amount, onClick, selected, isIncrement, variant, size = "small" }) => {
  const [isPressed, setIsPressed] = React.useState(false);

  const getLabel = () => {
    const formattedAmount = formatAmount(amount);
    if (isIncrement) {
      return variant === "subtract"
        ? `-BD ${formattedAmount}`
        : `+BD ${formattedAmount}`;
    }
    return `BD ${formattedAmount}`;
  };

  const getBgColor = () => {
    if (selected) return "rgb(237, 242, 247)";
    if (variant === "add") return "rgba(25, 118, 210, 0.08)";
    if (variant === "subtract") return "rgba(211, 47, 47, 0.08)";
    return "transparent";
  };

  const getBorderColor = () => {
    if (selected) return "#1976d2";
    if (variant === "add") return "rgba(25, 118, 210, 0.5)";
    if (variant === "subtract") return "rgba(211, 47, 47, 0.5)";
    return "rgba(0, 0, 0, 0.12)";
  };

  const getTextColor = () => {
    if (selected) return "#1976d2";
    if (variant === "add") return "rgb(25, 118, 210)";
    if (variant === "subtract") return "rgb(211, 47, 47)";
    return "inherit";
  };

  return (
    <Chip
      label={getLabel()}
      onClick={onClick}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      style={{
        backgroundColor: getBgColor(),
        border: `1px solid ${getBorderColor()}`,
        color: getTextColor(),
        borderRadius: 8,
        padding: size === "large" ? "8px 4px" : "6px 4px",
        cursor: "pointer",
        fontSize: size === "large" ? "1rem" : "0.875rem",
        height: "auto",
        width: "100%",
        minWidth: "unset",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
        transition: "all 0.2s ease",
        transform: isPressed ? "scale(0.95)" : "scale(1)",
        whiteSpace: "nowrap",
        display: "flex",
        justifyContent: "center",
      }}
    />
  );
};

export const PaymentForm: React.FC<PaymentFormProps> = ({
  open,
  onClose,
  onSubmit,
  cart,
}) => {
  const [showChange, setShowChange] = React.useState(false);

  const formik = useFormik({
    initialValues: {
      method: "cash" as PaymentMethod,
      amount: formatAmount(cart.total),
      reference: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const payment: PaymentData = {
        method: values.method,
        amount: formatAmount(values.amount),
        reference: values.reference,
        change:
          values.method === "cash"
            ? formatAmount(values.amount - cart.total)
            : undefined,
      };
      onSubmit(payment);
      onClose();
    },
  });

  React.useEffect(() => {
    if (formik.values.method === "cash" && formik.values.amount > cart.total) {
      setShowChange(false);
      const timer = setTimeout(() => setShowChange(true), 150);
      return () => clearTimeout(timer);
    }
    setShowChange(false);
    // Return undefined for all other code paths
    return undefined;
  }, [formik.values.amount, formik.values.method, cart.total]);

  const handleMethodChange = (method: PaymentMethod) => {
    formik.setFieldValue("method", method);
    if (method === "cash") {
      formik.setFieldValue("reference", "");
      formik.setFieldValue("amount", formatAmount(cart.total));
    } else {
      formik.setFieldValue("amount", formatAmount(cart.total));
    }
  };

  const handleQuickAmount = (amount: number) => {
    formik.setFieldValue("amount", formatAmount(amount));
  };

  const handleIncrementAmount = (increment: number) => {
    const currentAmount = formik.values.amount || cart.total;
    const newAmount = formatAmount(currentAmount + increment);
    // Only ensure amount doesn't go below 0
    formik.setFieldValue("amount", Math.max(0, newAmount));
  };

  // Quick increment amounts
  const incrementAmounts = [20, 10, 5, 1, 0.5, 0.1];

  // Generate quick amount options for cash payments
  const getQuickAmounts = () => {
    const roundedTotal = Math.ceil(cart.total);
    return [
      roundedTotal,
      roundedTotal + 10,
      roundedTotal + 20,
      roundedTotal + 50,
    ];
  };

  const dialogStyle = {
    borderRadius: 16,
    boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
    margin: "16px",
    width: "calc(100% - 32px)",
    maxWidth: "1200px",
    height: "calc(100% - 32px)",
    maxHeight: "800px",
    display: "flex",
    flexDirection: "column" as const,
    "@media (min-width: 600px)": {
      margin: "32px",
      width: "calc(100% - 64px)",
    },
  };

  const titleStyle = {
    paddingBottom: 16,
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    backgroundColor: "#fff",
  };

  const summaryPaperStyle = {
    padding: 24,
    paddingRight: 48,
    backgroundColor: "rgb(237, 242, 247)",
    borderRadius: 16,
    transition: "all 0.2s ease-in-out",
    height: "100%",
    minHeight: "200px",
    display: "flex",
    flexDirection: "column" as const,
  };

  const touchFriendlyButtonBase = {
    minHeight: "48px",
    minWidth: "48px",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    userSelect: "none" as const,
  };

  const toggleButtonStyle = {
    ...touchFriendlyButtonBase,
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 12,
    padding: "24px 12px",
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
    flex: 1,
    minWidth: 0,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.04)",
    },
    "&:active": {
      transform: "scale(0.98)",
    },
  };

  const selectedToggleButtonStyle = {
    ...toggleButtonStyle,
    backgroundColor: "rgb(237, 242, 247)",
    borderColor: "#1976d2",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  };

  const textFieldStyle = {
    borderRadius: 12,
  };

  const changeMessageStyle = {
    padding: 16,
    backgroundColor: "rgb(237, 247, 242)",
    borderRadius: 16,
    boxShadow: "0 4px 12px rgba(34, 154, 22, 0.1)",
  };

  const contentStyle = {
    display: "flex",
    flexDirection: "column" as const,
    flex: 1,
    overflow: "hidden",
    padding: 0,
  } as const;

  const mainContentStyle = {
    flex: 1,
    overflow: "auto",
    display: "flex",
    flexDirection: "column" as const,
    paddingBottom: "80px",
  } as const;

  const actionsStyle = {
    padding: "16px 24px",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    backgroundColor: "#fff",
    gap: 12,
    position: "sticky" as const,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.05)",
    marginTop: "auto",
  } as const;

  const buttonStyle = {
    borderRadius: 12,
    textTransform: "none" as const,
    minWidth: 120,
    padding: "10px 24px",
  };

  const getPaymentMethodColor = (method: PaymentMethod) =>
    formik.values.method === method ? "#1976d2" : "inherit";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ style: dialogStyle }}
      TransitionProps={{
        onExited: () => {
          formik.resetForm();
          setShowChange(false);
        },
      }}
    >
      <form
        onSubmit={formik.handleSubmit}
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DialogTitle style={titleStyle}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Process Payment
          </Typography>
        </DialogTitle>

        <DialogContent style={contentStyle}>
          <Box style={mainContentStyle}>
            <Grid
              container
              spacing={3}
              style={{
                flex: 1,
                minHeight: 0,
                height: "100%",
              }}
            >
              {/* Left Column - Order Summary */}
              <Grid
                item
                xs={12}
                md={4}
                style={{
                  height: "100%",
                  borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                  paddingRight: 24,
                }}
              >
                <Box
                  style={{
                    height: "100%",
                    overflow: "auto",
                    padding: "4px",
                  }}
                >
                  <Paper elevation={0} style={summaryPaperStyle}>
                    <Stack spacing={3}>
                      <Typography
                        variant="h6"
                        style={{ fontWeight: 600, color: "#1976d2" }}
                      >
                        Order Summary
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Stack spacing={2}>
                            <Typography variant="body1" color="text.secondary">
                              Items
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                              Subtotal
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                              VAT (10%)
                            </Typography>
                            <Typography
                              variant="h6"
                              style={{ fontWeight: 600 }}
                            >
                              Total Due
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={2} alignItems="flex-end">
                            <Typography variant="body1">
                              {cart.items.length}
                            </Typography>
                            <Typography variant="body1">
                              BD {formatAmount(cart.subtotal)}
                            </Typography>
                            <Typography variant="body1">
                              BD {formatAmount(cart.tax)}
                            </Typography>
                            <Typography
                              variant="h6"
                              style={{ fontWeight: 600, color: "#1976d2" }}
                            >
                              BD {formatAmount(cart.total)}
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Paper>
                </Box>
              </Grid>

              {/* Right Column - Payment Options */}
              <Grid item xs={12} md={8} style={{ height: "100%" }}>
                <Stack
                  spacing={4}
                  style={{ height: "100%", padding: "0 12px" }}
                >
                  {/* Payment Method Selection */}
                  <Box>
                    <Box style={{ display: "flex", gap: 12 }} sx={{ mt: 2 }}>
                      <ToggleButton
                        value="cash"
                        selected={formik.values.method === "cash"}
                        onChange={() => handleMethodChange("cash")}
                        style={
                          formik.values.method === "cash"
                            ? selectedToggleButtonStyle
                            : toggleButtonStyle
                        }
                      >
                        <Icon
                          className="material-icons"
                          style={{
                            fontSize: 32,
                            color: getPaymentMethodColor("cash"),
                          }}
                        >
                          payments
                        </Icon>
                        <Typography
                          variant="body1"
                          style={{
                            color: getPaymentMethodColor("cash"),
                            fontWeight:
                              formik.values.method === "cash" ? 600 : 400,
                          }}
                        >
                          Cash
                        </Typography>
                      </ToggleButton>
                      <ToggleButton
                        value="card"
                        selected={formik.values.method === "card"}
                        onChange={() => handleMethodChange("card")}
                        style={
                          formik.values.method === "card"
                            ? selectedToggleButtonStyle
                            : toggleButtonStyle
                        }
                      >
                        <Icon
                          className="material-icons"
                          style={{
                            fontSize: 32,
                            color: getPaymentMethodColor("card"),
                          }}
                        >
                          credit_card
                        </Icon>
                        <Typography
                          variant="body1"
                          style={{
                            color: getPaymentMethodColor("card"),
                            fontWeight:
                              formik.values.method === "card" ? 600 : 400,
                          }}
                        >
                          Card
                        </Typography>
                      </ToggleButton>
                      <ToggleButton
                        value="mobile"
                        selected={formik.values.method === "mobile"}
                        onChange={() => handleMethodChange("mobile")}
                        style={
                          formik.values.method === "mobile"
                            ? selectedToggleButtonStyle
                            : toggleButtonStyle
                        }
                      >
                        <Icon
                          className="material-icons"
                          style={{
                            fontSize: 32,
                            color: getPaymentMethodColor("mobile"),
                          }}
                        >
                          phone_android
                        </Icon>
                        <Typography
                          variant="body1"
                          style={{
                            color: getPaymentMethodColor("mobile"),
                            fontWeight:
                              formik.values.method === "mobile" ? 600 : 400,
                          }}
                        >
                          BenefitPay
                        </Typography>
                      </ToggleButton>
                    </Box>
                  </Box>

                  {/* Amount Input and Quick Actions */}
                  <Stack spacing={3}>
                    {/* Amount field and Quick Amounts side by side */}
                    <Grid container spacing={1} alignItems="flex-start">
                      <Grid
                        item
                        xs={12}
                        md={formik.values.method === "cash" ? 7 : 12}
                      >
                        <TextField
                          fullWidth
                          name="amount"
                          label="Amount Received"
                          type="number"
                          value={formik.values.amount}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.amount &&
                            Boolean(formik.errors.amount)
                          }
                          helperText={
                            formik.touched.amount && formik.errors.amount
                          }
                          InputProps={{
                            style: {
                              ...textFieldStyle,
                              fontSize: "2rem",
                              padding: "12px",
                              backgroundColor: "rgb(237, 242, 247)",
                            },
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography
                                  style={{ fontSize: "2rem", color: "#1976d2" }}
                                >
                                  BD
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      {formik.values.method === "cash" && (
                        <Grid
                          item
                          xs={12}
                          md={5}
                          sx={{ height: "-webkit-fill-available" }}
                        >
                          <Box
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gridTemplateRows: "1fr 1fr",
                              gap: 2,
                              height: "100%",
                            }}
                          >
                            {getQuickAmounts().map((amount) => (
                              <QuickAmountButton
                                key={amount}
                                amount={amount}
                                onClick={() => handleQuickAmount(amount)}
                                selected={formik.values.amount === amount}
                                size="large"
                              />
                            ))}
                          </Box>
                        </Grid>
                      )}
                    </Grid>

                    {formik.values.method === "cash" && (
                      <Stack spacing={3}>
                        {/* Quick Add/Subtract Grid */}
                        <Box>
                          <Grid container spacing={2}>
                            {/* Add Amount Column */}
                            <Grid item xs={6}>
                              <Box
                                style={{
                                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                                  padding: "12px",
                                  borderRadius: 12,
                                  border: "1px solid rgba(25, 118, 210, 0.1)",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="primary"
                                  style={{ marginBottom: 12, fontWeight: 500 }}
                                >
                                  Add Amount
                                </Typography>
                                <Box
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 8,
                                  }}
                                >
                                  {incrementAmounts.map((amount) => (
                                    <QuickAmountButton
                                      key={`add-${amount}`}
                                      amount={amount}
                                      onClick={() =>
                                        handleIncrementAmount(amount)
                                      }
                                      isIncrement
                                      variant="add"
                                      size="large"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            </Grid>

                            {/* Subtract Amount Column */}
                            <Grid item xs={6}>
                              <Box
                                style={{
                                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                                  padding: "12px",
                                  borderRadius: 12,
                                  border: "1px solid rgba(211, 47, 47, 0.1)",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="error"
                                  style={{ marginBottom: 12, fontWeight: 500 }}
                                >
                                  Subtract Amount
                                </Typography>
                                <Box
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 8,
                                  }}
                                >
                                  {incrementAmounts.map((amount) => (
                                    <QuickAmountButton
                                      key={`subtract-${amount}`}
                                      amount={amount}
                                      onClick={() =>
                                        handleIncrementAmount(-amount)
                                      }
                                      isIncrement
                                      variant="subtract"
                                      size="large"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Change Amount Display */}
                        <Fade in={true}>
                          <Paper
                            elevation={0}
                            style={{
                              ...changeMessageStyle,
                              padding: "24px",
                              backgroundColor:
                                formik.values.amount >= cart.total
                                  ? "rgb(237, 247, 242)"
                                  : "rgb(253, 237, 237)",
                            }}
                          >
                            <Stack spacing={2}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={2}
                                >
                                  <Icon
                                    style={{
                                      color:
                                        formik.values.amount >= cart.total
                                          ? "rgb(34, 154, 22)"
                                          : "rgb(211, 47, 47)",
                                      fontSize: "2.5rem",
                                    }}
                                  >
                                    {formik.values.amount >= cart.total
                                      ? "payments"
                                      : "warning"}
                                  </Icon>
                                  <Stack>
                                    <Typography
                                      variant="h5"
                                      style={{
                                        fontWeight: 600,
                                        color:
                                          formik.values.amount >= cart.total
                                            ? "rgb(34, 154, 22)"
                                            : "rgb(211, 47, 47)",
                                      }}
                                    >
                                      {formik.values.amount >= cart.total
                                        ? "Amount to Give to Customer"
                                        : "Amount to Take from Customer"}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      style={{
                                        fontWeight: 700,
                                        color:
                                          formik.values.amount >= cart.total
                                            ? "rgb(34, 154, 22)"
                                            : "rgb(211, 47, 47)",
                                      }}
                                    >
                                      BD{" "}
                                      {Math.abs(
                                        formatAmount(
                                          formik.values.amount - cart.total
                                        )
                                      )}
                                    </Typography>
                                  </Stack>
                                </Stack>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    style={{
                                      color: "text.secondary",
                                      textAlign: "right",
                                    }}
                                  >
                                    Amount Received
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    style={{
                                      fontWeight: 600,
                                      color:
                                        formik.values.amount >= cart.total
                                          ? "rgb(34, 154, 22)"
                                          : "rgb(211, 47, 47)",
                                    }}
                                  >
                                    BD {formatAmount(formik.values.amount)}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Stack>
                          </Paper>
                        </Fade>
                      </Stack>
                    )}
                  </Stack>

                  {/* Reference Number Input for non-cash payments */}
                  {formik.values.method !== "cash" && (
                    <TextField
                      fullWidth
                      name="reference"
                      label="Reference Number"
                      placeholder={
                        formik.values.method === "card"
                          ? "Last 4 digits of card"
                          : "Transaction reference"
                      }
                      value={formik.values.reference}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.reference &&
                        Boolean(formik.errors.reference)
                      }
                      helperText={
                        formik.touched.reference && formik.errors.reference
                      }
                      InputProps={{
                        style: {
                          ...textFieldStyle,
                          fontSize: "1.2rem",
                          padding: "12px 0",
                        },
                        startAdornment: formik.values.method === "card" && (
                          <InputAdornment position="start">
                            <Icon
                              style={{
                                color: "rgba(0, 0, 0, 0.54)",
                                fontSize: "1.5rem",
                              }}
                            >
                              tag
                            </Icon>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions style={actionsStyle}>
          <Button
            onClick={onClose}
            style={{
              ...buttonStyle,
              ...touchFriendlyButtonBase,
            }}
            size="large"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={!formik.isValid || formik.values.amount < cart.total}
            style={{
              ...buttonStyle,
              ...touchFriendlyButtonBase,
            }}
          >
            Complete Payment
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
